/**
 * Training Mode topic picker modal — 3-column grid, search, progress
 * badges, role-filtered (spec §1B + §6).
 *
 * Data source: GET /api/training/topics + GET /api/training/progress.
 * Real mode + demo mode share the same API surface — demo users get
 * their own isolated `demo_db` rows.
 */
import { useEffect, useMemo, useState } from "react";
import { listTopics, getProgress } from "@/lib/trainingApi";
import { useTraining, useOverlayAutoMinimize } from "@/lib/trainingContext";
import { X } from "lucide-react";
import { toast } from "sonner";

export default function TrainingTopicPicker({ onPickTopic }) {
  const { pickerOpen, setPickerOpen, activeTopic } = useTraining();
  useOverlayAutoMinimize(pickerOpen);

  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [progress, setProgress] = useState(null);
  const [requiredIds, setRequiredIds] = useState([]);
  const [disabledByAdmin, setDisabledByAdmin] = useState(false);

  useEffect(() => {
    if (!pickerOpen) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [tRes, pRes] = await Promise.all([
          listTopics(),
          getProgress().catch(() => null),
        ]);
        if (cancelled) return;
        setTopics(tRes.topics || []);
        setRequiredIds(tRes.required_topic_ids || []);
        setDisabledByAdmin(!!tRes.disabled_by_admin);
        setProgress(pRes);
      } catch {
        if (!cancelled) toast.error("Failed to load training topics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pickerOpen]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = topics.filter((t) => {
      if (!q) return true;
      return (
        (t.title || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.slug || "").toLowerCase().includes(q) ||
        (t.category || "").toLowerCase().includes(q)
      );
    });
    const map = {};
    filtered.forEach((t) => {
      const cat = t.category || "General";
      (map[cat] = map[cat] || []).push(t);
    });
    // Sort: required first, then title
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => {
        const ar = requiredIds.includes(a.id) ? 0 : 1;
        const br = requiredIds.includes(b.id) ? 0 : 1;
        if (ar !== br) return ar - br;
        return (a.title || "").localeCompare(b.title || "");
      });
    });
    return map;
  }, [topics, query, requiredIds]);

  const completedSlugs = useMemo(() => {
    const set = new Set();
    (progress?.completed_topics || []).forEach((row) => {
      if (row?.slug) set.add(row.slug);
    });
    return set;
  }, [progress]);

  const inProgressMap = useMemo(() => {
    return progress?.in_progress || {};
  }, [progress]);

  if (!pickerOpen) return null;

  return (
    <div
      data-testid="training-picker-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.3)",
        zIndex: 220,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "48px 16px",
        overflowY: "auto",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) setPickerOpen(false); }}
    >
      <div
        data-testid="training-picker-modal"
        style={{
          width: 640,
          maxWidth: "92vw",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          maxHeight: "88vh",
          overflowY: "auto",
          padding: 22,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 500, color: "#111" }}>
              What would you like to learn today?
            </div>
            <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>
              Choose a topic or type a question — your AI trainer will guide you through it.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen(false)}
            data-testid="training-picker-close"
            aria-label="Close topic picker"
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 4 }}
          >
            <X style={{ width: 18, height: 18, color: "#888" }} />
          </button>
        </div>

        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. 'How do I import leads?' or 'Send a quote to a lead'"
          data-testid="training-picker-search"
          style={{
            width: "100%",
            border: "0.5px solid #ddd",
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 14,
            marginTop: 14,
            marginBottom: 14,
            outline: "none",
            boxSizing: "border-box",
          }}
        />

        {disabledByAdmin && (
          <div
            data-testid="training-disabled-by-admin"
            style={{
              background: "#FFF4E5",
              border: "0.5px solid #F3A84C",
              color: "#7A4E0A",
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            Training Mode is currently disabled for your role by your admin.
          </div>
        )}

        {loading && (
          <div data-testid="training-picker-loading" style={{ textAlign: "center", padding: 24, color: "#888", fontSize: 12 }}>
            Loading topics…
          </div>
        )}

        {!loading && Object.keys(grouped).length === 0 && (
          <div data-testid="training-picker-empty" style={{ textAlign: "center", padding: 24, color: "#888", fontSize: 13 }}>
            No topics match your search. Try a different keyword.
          </div>
        )}

        {!loading && Object.entries(grouped).map(([category, list]) => (
          <div key={category} style={{ marginTop: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
              {category}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              {list.map((t) => {
                const completed = completedSlugs.has(t.slug);
                const ip = inProgressMap[t.slug];
                const inProgress = ip && !completed;
                const isSelected = activeTopic?.slug === t.slug;
                const isRequired = requiredIds.includes(t.id);
                const stepsTotal = (t.steps || []).length || 1;
                const progressPct = inProgress ? Math.min(100, Math.max(4, Math.round(((ip.current_step || 0) / stepsTotal) * 100))) : 0;

                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { onPickTopic(t); }}
                    data-testid={`training-topic-card-${t.slug}`}
                    style={{
                      textAlign: "left",
                      background: isSelected ? "#f0f7ff" : "#fff",
                      border: isSelected ? "2px solid #2563eb" : "0.5px solid #eeeeee",
                      borderLeft: completed ? "3px solid #1D9E75" : (isSelected ? "2px solid #2563eb" : "0.5px solid #eeeeee"),
                      borderRadius: 10,
                      padding: "12px 14px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {completed && (
                      <span
                        data-testid={`training-topic-completed-${t.slug}`}
                        aria-label="Completed"
                        title="Completed"
                        style={{
                          position: "absolute",
                          top: 8, right: 8,
                          width: 18, height: 18,
                          borderRadius: "50%",
                          background: "#EAF3DE",
                          color: "#3B6D11",
                          fontSize: 11,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700,
                        }}
                      >✓</span>
                    )}
                    {isRequired && (
                      <span
                        data-testid={`training-topic-required-${t.slug}`}
                        style={{
                          fontSize: 9, fontWeight: 600, color: "#9A1C1C", background: "#FDE2E2",
                          padding: "1px 6px", borderRadius: 10, alignSelf: "flex-start",
                          textTransform: "uppercase", letterSpacing: 0.4,
                        }}
                      >Required</span>
                    )}
                    <div style={{ fontSize: 18 }} aria-hidden>{t.icon || "🎓"}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#111" }}>{t.title}</div>
                    {t.description && (
                      <div style={{ fontSize: 11, color: "#888", lineHeight: 1.4 }}>{t.description}</div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                      {t.role_badge && (
                        <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 10, background: "#f0f7ff", color: "#185FA5" }}>
                          {t.role_badge}
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: "#888" }}>
                        ~{t.estimated_minutes || 1} min
                      </span>
                    </div>
                    {inProgress && (
                      <div
                        data-testid={`training-topic-progress-${t.slug}`}
                        style={{
                          position: "absolute",
                          bottom: 0, left: 0, right: 0,
                          height: 3, background: "#eeeeee",
                        }}
                      >
                        <div style={{ width: `${progressPct}%`, height: "100%", background: "#2563eb" }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div
          onClick={() => setPickerOpen(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") setPickerOpen(false); }}
          style={{ textAlign: "center", marginTop: 18, color: "#888", fontSize: 12, cursor: "pointer" }}
          data-testid="training-picker-close-link"
        >
          Close
        </div>
      </div>
    </div>
  );
}
